/**
 * Block explorer URLs aligned with ptadmin usage (Snowtrace, Immutable explorer).
 * @see UserDetails.js, TrackedWallets.js, ContractsManager.js
 */

/** V2 mint pipeline executor chain (avax-cchain). */
export const MINT_EXECUTOR_CHAIN_ID = 43114;

const ADDRESS_BASE_BY_CHAIN_ID = {
  43114: 'https://snowtrace.io/address',
  13371: 'https://explorer.immutable.com/address',
};

const TX_BASE_BY_CHAIN_ID = {
  43114: 'https://snowtrace.io/tx',
  13371: 'https://explorer.immutable.com/tx',
};

/**
 * Per-chain URL for a single NFT / token instance (legacy source token on that chain).
 * @param {unknown} chainId
 * @param {string} contract 0x-prefixed contract
 * @param {string | number} tokenId on-chain id as stored (decimal or hex string)
 * @returns {string | null}
 */
export function explorerSourceTokenHref(chainId, contract, tokenId) {
  const n = Number(chainId);
  if (!Number.isFinite(n)) return null;
  const c = normalizeEvmAddress(contract);
  if (!c) return null;
  const tid = tokenId == null ? '' : String(tokenId).trim();
  if (!tid) return null;
  const enc = encodeURIComponent(tid);
  if (n === 43114) {
    return `https://snowtrace.io/nft/${c}/${enc}`;
  }
  if (n === 13371) {
    return `https://explorer.immutable.com/token/${c}/instance/${enc}`;
  }
  return null;
}

/** @param {unknown} addr */
function normalizeEvmAddress(addr) {
  if (addr == null) return null;
  const s = String(addr).trim().toLowerCase();
  if (!/^0x[0-9a-f]{40}$/.test(s)) return null;
  return s;
}

/** Human label for tooltips (Mint pipeline groups). */
export function explorerSiteLabel(chainId) {
  const n = Number(chainId);
  if (n === 43114) return 'Snowtrace (Avalanche C-Chain)';
  if (n === 13371) return 'Immutable explorer (zkEVM)';
  if (!Number.isFinite(n)) return 'Unknown chain';
  return `Chain ${n}`;
}

/** @param {unknown} chainId */
export function explorerAddressHref(chainId) {
  const n = Number(chainId);
  if (!Number.isFinite(n)) return null;
  return ADDRESS_BASE_BY_CHAIN_ID[n] ?? null;
}

/** @param {unknown} chainId */
export function explorerTxHref(chainId) {
  const n = Number(chainId);
  if (!Number.isFinite(n)) return null;
  return TX_BASE_BY_CHAIN_ID[n] ?? null;
}

/**
 * Full explorer URL for a transaction on chainId (Snowtrace / Immutascan-style bases).
 * @param {unknown} chainId
 * @param {string | null | undefined} txHash
 * @returns {string | null}
 */
export function txUrl(chainId, txHash) {
  const base = explorerTxHref(chainId);
  if (!base || txHash == null) return null;
  const norm = String(txHash).trim().toLowerCase();
  if (!/^0x[0-9a-f]+$/.test(norm)) return null;
  return `${base}/${norm}`;
}

/**
 * Middle-ellipsis display for 0x-prefixed hashes (addresses ~42 chars, tx ~66).
 * @param {string | null | undefined} value
 */
export function shorten0x(value, headChars = 6, tailChars = 4) {
  if (value == null) return '';
  const s = String(value).trim();
  if (!s) return '';
  const lower = s.toLowerCase();
  const hexBody = lower.startsWith('0x') ? lower.slice(2) : lower;
  if (!/^[0-9a-f]+$/i.test(hexBody)) {
    return s.length > headChars + tailChars + 1 ? `${s.slice(0, headChars)}…${s.slice(-tailChars)}` : s;
  }
  const prefix = lower.startsWith('0x') ? '0x' : '';
  const body = lower.startsWith('0x') ? lower.slice(2) : lower;
  if (body.length <= headChars + tailChars) return `${prefix}${body}`;
  return `${prefix}${body.slice(0, headChars)}…${body.slice(-tailChars)}`;
}
